import { useMatch, useParams, useSearchParams } from 'react-router-dom';
import { CloseButton } from '@headlessui/react';
import {
  LocationCard,
  LocationCardBaseProps,
  LocationCardProps,
} from '@mosey/components/layout/LocationCard';
import { useLocationId } from '@mosey/components/hooks/navigation';
import {
  useQuestionTasksSummary,
  useRequirementTasksSummary,
  useTasksOverviewData,
} from '../../utils/hooks';
import { useUser } from '../../../../hooks/useUser';
import { getTodoCountForLocation } from '../../../locations/utils';

export type RegionFilterOnClickFn = (
  region: LocationCardProps<typeof CloseButton>['region'],
) => void;

export type RegionFilterProps = Omit<
  LocationCardProps<typeof CloseButton>,
  'as'
> & {
  onClick?: RegionFilterOnClickFn;
};

export const RegionFilter = (props: RegionFilterProps) => {
  const { region, onClick } = props;
  const isHome = useMatch('/home');
  const { locationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const code = region.id.toLowerCase();
  const isCurrentLocation = code === locationId;

  if (isHome) {
    return <TasksOverviewRegionFilter {...props} />;
  }

  return (
    <LocationCard
      {...props}
      as={CloseButton}
      onClick={() => {
        if (onClick) {
          onClick(region);
        } else {
          searchParams.set('location_id', code);
          setSearchParams(searchParams);
        }
      }}
      aria-pressed={isCurrentLocation}
      selected={isCurrentLocation}
    />
  );
};

export const TasksOverviewRegionFilter = (
  props: Omit<LocationCardProps<typeof CloseButton>, 'as'>,
) => {
  const { region } = props;
  const {
    legal_entity: { incorporation_region: incorporationRegion },
  } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { summary } = useTasksOverviewData();
  const questionTasksSummary = useQuestionTasksSummary();
  const regionQuestionTasksSummary = questionTasksSummary[region.id];
  const requirementTasksSummary = useRequirementTasksSummary();
  const regionRequirementTasksSummary = requirementTasksSummary[region.id];
  const locationId = useLocationId();
  const code = region.id.toLowerCase();
  const isCurrentLocation = code === locationId;
  const isAssessment = regionQuestionTasksSummary?.setup_count.todo > 0;
  const isSetup = regionRequirementTasksSummary?.setup_count.todo > 0;
  const hasOverdueTasks =
    regionRequirementTasksSummary &&
    regionRequirementTasksSummary.overdue_count.todo > 0;
  const notification: string[] = [];
  let status: LocationCardBaseProps['status'] = undefined;

  if (hasOverdueTasks) {
    status = 'overdue';
  } else if (isSetup) {
    status = 'setup';
  } else if (isAssessment) {
    status = 'assessment';
  } else if (incorporationRegion?.id === region.id) {
    status = 'home';
  }

  if (getTodoCountForLocation(summary, region.id) > 0) {
    notification.push('action required');
  }

  return (
    <LocationCard
      {...props}
      as={CloseButton}
      onClick={() => {
        searchParams.set('location_id', code);
        setSearchParams(searchParams);
      }}
      aria-pressed={isCurrentLocation}
      aria-label={`Show ${region.name} tasks`}
      selected={isCurrentLocation}
      notification={notification.join(', ')}
      status={status}
    />
  );
};
